import { apiFetch } from "../api.js";
import type {
  CreateWatchlistInput,
  UpdateWatchlistInput,
  Watchlist,
  WatchlistsListResponse,
} from "./types.js";

/**
 * Watchlists API module — household-scoped CRUD landed in Phase 2
 * Session 2.2a. The backend resolves household from the JWT, so the
 * frontend never passes household_id.
 *
 * Soft-delete: `deleteWatchlist` returns 204 and the row stays in DDB
 * with `is_archived=true`. Use `?include_archived=true` on list to see
 * archived rows.
 */

export function listWatchlists(): Promise<WatchlistsListResponse> {
  return apiFetch<WatchlistsListResponse>("/v1/watchlists");
}

export function createWatchlist(
  input: CreateWatchlistInput,
): Promise<Watchlist> {
  return apiFetch<Watchlist>("/v1/watchlists", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateWatchlist(
  watchlistId: string,
  input: UpdateWatchlistInput,
): Promise<Watchlist> {
  return apiFetch<Watchlist>(
    `/v1/watchlists/${encodeURIComponent(watchlistId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function deleteWatchlist(watchlistId: string): Promise<void> {
  return apiFetch<void>(
    `/v1/watchlists/${encodeURIComponent(watchlistId)}`,
    { method: "DELETE" },
  );
}

/**
 * Normalize ticker input from a comma-separated string to a deduped,
 * uppercased array. Used by the create/update form before submit.
 *
 * Backend accepts whatever shape we send (it filters via TICKER_RE on
 * its side) but doing it client-side is the better UX: the user sees
 * the cleaned list before they submit, and we don't waste a round-trip
 * to learn that a duplicate or whitespace was rejected.
 */
export function normalizeTickerInput(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(",")
        .map((t) => t.trim().toUpperCase())
        .filter((t) => t.length > 0),
    ),
  );
}
